import React, { FormEvent, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import {
  CITY_OPTIONS,
  COLLECTIONS,
  formatCoords,
  pointAppUri,
  pointShareUrl,
  type MockPlaylist,
  type StationaryPoint,
} from '../../data/stationary-mock';

export type StationaryDialog =
  | { type: 'add' }
  | { type: 'edit-name'; pointId: string }
  | { type: 'edit-accuracy'; pointId: string }
  | { type: 'remove'; pointIds: string[] }
  | { type: 'share'; pointId: string }
  | { type: 'shortcut'; pointId: string }
  | { type: 'playlist'; pointId: string }
  | { type: 'collections' };

export interface NewPointInput {
  name: string;
  address: string;
  city: string;
  latitude: number;
  longitude: number;
  accuracyMeters: number;
  notes: string;
  collectionId: string;
}

interface DialogLayerProps {
  dialog: StationaryDialog;
  points: StationaryPoint[];
  playlists: MockPlaylist[];
  activeCollectionId: string | null;
  onClose(): void;
  onCreate(input: NewPointInput): void;
  onSaveName(pointId: string, name: string, notes: string): void;
  onSaveAccuracy(pointId: string, meters: number): void;
  onRemove(pointIds: string[]): void;
  onPickPlaylist(pointId: string, playlistId: string): void;
  onCreatePlaylist(pointId: string, name: string): void;
  onPickCollection(collectionId: string): void;
  onToast(message: string): void;
}

export async function copyText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    /* Fall through to the textarea copy path. */
  }
  try {
    const area = document.createElement('textarea');
    area.value = value;
    area.setAttribute('readonly', 'true');
    area.style.position = 'fixed';
    area.style.left = '-9999px';
    document.body.appendChild(area);
    area.select();
    const copied = document.execCommand('copy');
    area.remove();
    return copied;
  } catch {
    return false;
  }
}

function Modal({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string;
  subtitle?: string;
  onClose(): void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div className="sp-modal" onMouseDown={onClose}>
      <div
        className="sp-modal__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sp-dialog-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="sp-modal__head">
          <div>
            <h2 id="sp-dialog-title">{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button type="button" className="sp-icon-btn" aria-label="Close dialog" onClick={onClose}>
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function AddDialog({ onClose, onCreate }: { onClose(): void; onCreate(input: NewPointInput): void }) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState<string>(CITY_OPTIONS[0]);
  const [latitude, setLatitude] = useState('28.6139');
  const [longitude, setLongitude] = useState('77.2090');
  const [accuracy, setAccuracy] = useState('5');
  const [notes, setNotes] = useState('');
  const [collectionId, setCollectionId] = useState('');
  const [error, setError] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const lat = Number(latitude);
    const lng = Number(longitude);
    const meters = Number(accuracy);
    if (!name.trim()) {
      setError('Enter a name.');
      return;
    }
    if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
      setError('Latitude must be between -90 and 90.');
      return;
    }
    if (!Number.isFinite(lng) || lng < -180 || lng > 180) {
      setError('Longitude must be between -180 and 180.');
      return;
    }
    if (!Number.isFinite(meters) || meters < 1 || meters > 500) {
      setError('Accuracy must be between 1 and 500 meters.');
      return;
    }
    onCreate({
      name: name.trim(),
      address: address.trim() || `${city}, India`,
      city,
      latitude: lat,
      longitude: lng,
      accuracyMeters: Math.round(meters),
      notes: notes.trim(),
      collectionId,
    });
  };

  return (
    <Modal title="Add stationary point" subtitle="Saved on this screen until the API is connected." onClose={onClose}>
      <form onSubmit={submit}>
        <label className="sp-field">
          Name
          <input value={name} onChange={(event) => setName(event.target.value)} autoFocus required />
        </label>
        <label className="sp-field">
          Address
          <input value={address} onChange={(event) => setAddress(event.target.value)} placeholder="New Delhi, Delhi, India" />
        </label>
        <label className="sp-field">
          City
          <select value={city} onChange={(event) => setCity(event.target.value)}>
            {CITY_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </label>
        <div className="sp-field-row">
          <label className="sp-field">
            Latitude
            <input value={latitude} onChange={(event) => setLatitude(event.target.value)} inputMode="decimal" />
          </label>
          <label className="sp-field">
            Longitude
            <input value={longitude} onChange={(event) => setLongitude(event.target.value)} inputMode="decimal" />
          </label>
        </div>
        <label className="sp-field">
          Accuracy (meters)
          <input value={accuracy} onChange={(event) => setAccuracy(event.target.value)} inputMode="numeric" />
        </label>
        <label className="sp-field">
          Collection
          <select value={collectionId} onChange={(event) => setCollectionId(event.target.value)}>
            <option value="">None</option>
            {COLLECTIONS.map((collection) => (
              <option key={collection.id} value={collection.id}>{collection.name}</option>
            ))}
          </select>
        </label>
        <label className="sp-field">
          Notes
          <textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={3} />
        </label>
        {error && <p className="sp-form-error">{error}</p>}
        <div className="sp-modal__actions">
          <button type="button" className="sp-btn sp-btn--ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="sp-btn sp-btn--primary">Save point</button>
        </div>
      </form>
    </Modal>
  );
}

function EditNameDialog({
  point,
  onClose,
  onSave,
}: {
  point: StationaryPoint;
  onClose(): void;
  onSave(name: string, notes: string): void;
}) {
  const [name, setName] = useState(point.name);
  const [notes, setNotes] = useState(point.notes);
  const [error, setError] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      setError('Enter a name.');
      return;
    }
    onSave(name.trim(), notes.trim());
  };

  return (
    <Modal title="Edit name & notes" subtitle={formatCoords(point.latitude, point.longitude)} onClose={onClose}>
      <form onSubmit={submit}>
        <label className="sp-field">
          Name
          <input value={name} onChange={(event) => setName(event.target.value)} autoFocus />
        </label>
        <label className="sp-field">
          Notes
          <textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={4} />
        </label>
        {error && <p className="sp-form-error">{error}</p>}
        <div className="sp-modal__actions">
          <button type="button" className="sp-btn sp-btn--ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="sp-btn sp-btn--primary">Save</button>
        </div>
      </form>
    </Modal>
  );
}

function EditAccuracyDialog({
  point,
  onClose,
  onSave,
}: {
  point: StationaryPoint;
  onClose(): void;
  onSave(meters: number): void;
}) {
  const [accuracy, setAccuracy] = useState(String(point.accuracyMeters));
  const [error, setError] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const meters = Number(accuracy);
    if (!Number.isFinite(meters) || meters < 1 || meters > 500) {
      setError('Accuracy must be between 1 and 500 meters.');
      return;
    }
    onSave(Math.round(meters));
  };

  return (
    <Modal title="Edit accuracy" subtitle={`Current accuracy is ${point.accuracyMeters} m.`} onClose={onClose}>
      <form onSubmit={submit}>
        <div className="sp-presets">
          {[1, 5, 10, 20, 50].map((meters) => (
            <button
              key={meters}
              type="button"
              className="sp-chip"
              aria-pressed={accuracy === String(meters)}
              onClick={() => setAccuracy(String(meters))}
            >
              {meters} m
            </button>
          ))}
        </div>
        <label className="sp-field">
          Meters
          <input value={accuracy} onChange={(event) => setAccuracy(event.target.value)} inputMode="numeric" autoFocus />
        </label>
        {error && <p className="sp-form-error">{error}</p>}
        <div className="sp-modal__actions">
          <button type="button" className="sp-btn sp-btn--ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="sp-btn sp-btn--primary">Save accuracy</button>
        </div>
      </form>
    </Modal>
  );
}

function RemoveDialog({
  points,
  onClose,
  onRemove,
}: {
  points: StationaryPoint[];
  onClose(): void;
  onRemove(): void;
}) {
  const title = points.length === 1 ? `Remove ${points[0].name} from history?` : `Remove ${points.length} points from history?`;
  return (
    <Modal title={title} subtitle="This deletes the saved point from this list." onClose={onClose}>
      <div className="sp-modal__actions">
        <button type="button" className="sp-btn sp-btn--ghost" onClick={onClose}>Cancel</button>
        <button type="button" className="sp-btn sp-btn--danger" onClick={onRemove}>Remove</button>
      </div>
    </Modal>
  );
}

function ShareDialog({ point, onClose, onToast }: { point: StationaryPoint; onClose(): void; onToast(message: string): void }) {
  const url = pointShareUrl(point);

  const copy = async () => {
    const copied = await copyText(url);
    onToast(copied ? 'Share link copied' : 'Could not copy the link');
  };

  const share = async () => {
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: point.name, text: point.address, url });
        onClose();
        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
      }
    }
    await copy();
  };

  return (
    <Modal title="Share point" subtitle={point.name} onClose={onClose}>
      <label className="sp-field">
        Link
        <input value={url} readOnly />
      </label>
      <div className="sp-modal__actions">
        <button type="button" className="sp-btn sp-btn--ghost" onClick={copy}>Copy link</button>
        <button type="button" className="sp-btn sp-btn--primary" onClick={share}>Share</button>
      </div>
    </Modal>
  );
}

function ShortcutDialog({ point, onClose, onToast }: { point: StationaryPoint; onClose(): void; onToast(message: string): void }) {
  const copyApp = async () => {
    const copied = await copyText(pointAppUri(point));
    onToast(copied ? 'Shortcut link copied' : 'Could not copy the link');
  };
  const copyWeb = async () => {
    const copied = await copyText(pointShareUrl(point));
    onToast(copied ? 'Web link copied' : 'Could not copy the link');
  };

  return (
    <Modal
      title="Create shortcut"
      subtitle="Android pins this point on the home screen. Copy the app link to open the same point in MockLoc."
      onClose={onClose}
    >
      <div className="sp-modal__actions">
        <button type="button" className="sp-btn sp-btn--ghost" onClick={copyWeb}>Copy web link</button>
        <button type="button" className="sp-btn sp-btn--primary" onClick={copyApp}>Copy app link</button>
      </div>
    </Modal>
  );
}

function PlaylistDialog({
  point,
  playlists,
  onClose,
  onPick,
  onCreate,
}: {
  point: StationaryPoint;
  playlists: MockPlaylist[];
  onClose(): void;
  onPick(playlistId: string): void;
  onCreate(name: string): void;
}) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const create = (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      setError('Name required');
      return;
    }
    onCreate(name.trim());
  };

  return (
    <Modal title="Add to playlist" subtitle={`Choose a playlist for “${point.name}”.`} onClose={onClose}>
      <ul className="sp-choice-list">
        {playlists.map((playlist) => {
          const already = point.playlistIds.includes(playlist.id);
          return (
            <li key={playlist.id}>
              <button type="button" onClick={() => onPick(playlist.id)}>
                <strong>{playlist.name}</strong>
                <small>{already ? 'Already added' : 'Add'}</small>
              </button>
            </li>
          );
        })}
      </ul>
      <form onSubmit={create} className="sp-create-row">
        <label className="sp-field">
          New playlist
          <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Playlist name" />
        </label>
        {error && <p className="sp-form-error">{error}</p>}
        <button type="submit" className="sp-btn sp-btn--primary">Create and add</button>
      </form>
    </Modal>
  );
}

function CollectionsDialog({
  points,
  activeCollectionId,
  onClose,
  onPick,
}: {
  points: StationaryPoint[];
  activeCollectionId: string | null;
  onClose(): void;
  onPick(collectionId: string): void;
}) {
  return (
    <Modal title="Collections" subtitle="Filter the list by a saved collection." onClose={onClose}>
      <ul className="sp-choice-list">
        {COLLECTIONS.map((collection) => {
          const count = points.filter((point) => point.collectionIds.includes(collection.id)).length;
          return (
            <li key={collection.id}>
              <button type="button" aria-pressed={activeCollectionId === collection.id} onClick={() => onPick(collection.id)}>
                <span className="sp-dot" style={{ background: collection.color }} aria-hidden="true" />
                <strong>{collection.name}</strong>
                <small>{count}</small>
              </button>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}

const StationaryDialogs: React.FC<DialogLayerProps> = ({
  dialog,
  points,
  playlists,
  activeCollectionId,
  onClose,
  onCreate,
  onSaveName,
  onSaveAccuracy,
  onRemove,
  onPickPlaylist,
  onCreatePlaylist,
  onPickCollection,
  onToast,
}) => {
  if (dialog.type === 'add') return <AddDialog onClose={onClose} onCreate={onCreate} />;
  if (dialog.type === 'collections') {
    return (
      <CollectionsDialog
        points={points}
        activeCollectionId={activeCollectionId}
        onClose={onClose}
        onPick={onPickCollection}
      />
    );
  }

  const point = 'pointId' in dialog ? points.find((item) => item.id === dialog.pointId) : undefined;
  if (dialog.type === 'remove') {
    const selected = points.filter((item) => dialog.pointIds.includes(item.id));
    if (selected.length === 0) return null;
    return <RemoveDialog points={selected} onClose={onClose} onRemove={() => onRemove(dialog.pointIds)} />;
  }
  if (!point) return null;
  if (dialog.type === 'edit-name') {
    return <EditNameDialog point={point} onClose={onClose} onSave={(name, notes) => onSaveName(point.id, name, notes)} />;
  }
  if (dialog.type === 'edit-accuracy') {
    return <EditAccuracyDialog point={point} onClose={onClose} onSave={(meters) => onSaveAccuracy(point.id, meters)} />;
  }
  if (dialog.type === 'share') return <ShareDialog point={point} onClose={onClose} onToast={onToast} />;
  if (dialog.type === 'shortcut') return <ShortcutDialog point={point} onClose={onClose} onToast={onToast} />;
  return (
    <PlaylistDialog
      point={point}
      playlists={playlists}
      onClose={onClose}
      onPick={(playlistId) => onPickPlaylist(point.id, playlistId)}
      onCreate={(name) => onCreatePlaylist(point.id, name)}
    />
  );
};

export default StationaryDialogs;
